// src/components/RichTextEditor.jsx
//
// Lightweight, dependency-free rich text editor for the wizard's
// narrative fields (Project Introduction, etc.) -- per request, "Project
// Introduction" is the first field converted from a plain textarea to
// Rich Text, with a formatting toolbar that starts collapsed and can be
// toggled open.
//
// No new npm package (react-quill / tiptap / draft-js / slate) is
// installed in this project, and adding one would need `npm install`
// run manually on the developer's machine before it would work -- so
// this is built from a plain contentEditable <div> instead, styled to
// match the existing Bootstrap ".form-control" textareas it replaces.
// Formatting commands (Bold/Italic/Underline/Bullet list/Numbered list)
// use document.execCommand, which is deprecated but still works in every
// Chromium/Edge browser, which is all this internal tool needs to run
// in.
//
// Usage is drop-in compatible with the <textarea> it replaces: it takes
// a `name`, a `value` (now an HTML string instead of plain text) and an
// `onChange` that receives the same shape a native onChange event does
// ({ target: { name, value } }), so the existing handleChange(e) =>
// setForm(f => ({ ...f, [e.target.name]: e.target.value })) pattern used
// throughout the wizard keeps working unmodified.
import { useEffect, useRef, useState } from "react";

export default function RichTextEditor({
  name,
  value,
  onChange,
  placeholder = "",
  disabled = false,
  minHeight = 78, // ~ matches the rows="3" textareas this replaces
}) {
  const editorRef = useRef(null);
  const lastValueRef = useRef(value || "");
  const [toolbarOpen, setToolbarOpen] = useState(false);

  // Keep the contentEditable DOM in sync with the controlled `value`
  // prop, but only when the change came from OUTSIDE this component
  // (e.g. a different saved project's data just loaded) -- overwriting
  // innerHTML on every keystroke would reset the caret position.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (value !== lastValueRef.current && document.activeElement !== el) {
      el.innerHTML = value || "";
      lastValueRef.current = value || "";
    }
  }, [value]);

  const emitChange = () => {
    const html = editorRef.current ? editorRef.current.innerHTML : "";
    lastValueRef.current = html;
    if (onChange) onChange({ target: { name, value: html } });
  };

  const runCommand = (command) => {
    if (disabled) return;
    editorRef.current?.focus();
    document.execCommand(command, false, undefined);
    emitChange();
  };

  return (
    <div className={`rte${disabled ? " rte-disabled" : ""}`}>
      <button
        type="button"
        className="rte-toggle"
        onClick={() => setToolbarOpen((open) => !open)}
        disabled={disabled}
        title={toolbarOpen ? "Hide formatting toolbar" : "Show formatting toolbar"}
      >
        <span className={`rte-caret${toolbarOpen ? " rte-caret-open" : ""}`}>▸</span> Formatting
      </button>

      {toolbarOpen && !disabled && (
        <div className="rte-toolbar" role="toolbar" aria-label="Text formatting">
          <div className="btn-group btn-group-sm me-1" role="group">
            <button type="button" className="btn btn-outline-secondary fw-bold" onMouseDown={(e) => e.preventDefault()} onClick={() => runCommand("bold")} title="Bold">
              B
            </button>
            <button type="button" className="btn btn-outline-secondary fst-italic" onMouseDown={(e) => e.preventDefault()} onClick={() => runCommand("italic")} title="Italic">
              I
            </button>
            <button type="button" className="btn btn-outline-secondary text-decoration-underline" onMouseDown={(e) => e.preventDefault()} onClick={() => runCommand("underline")} title="Underline">
              U
            </button>
          </div>
          <div className="btn-group btn-group-sm me-1" role="group">
            <button type="button" className="btn btn-outline-secondary" onMouseDown={(e) => e.preventDefault()} onClick={() => runCommand("insertUnorderedList")} title="Bullet list">
              • List
            </button>
            <button type="button" className="btn btn-outline-secondary" onMouseDown={(e) => e.preventDefault()} onClick={() => runCommand("insertOrderedList")} title="Numbered list">
              1. List
            </button>
          </div>
          <div className="btn-group btn-group-sm" role="group">
            <button type="button" className="btn btn-outline-secondary" onMouseDown={(e) => e.preventDefault()} onClick={() => runCommand("justifyLeft")} title="Align left">
              <span className="rte-align rte-align-left"><span /><span /><span /><span /></span>
            </button>
            <button type="button" className="btn btn-outline-secondary" onMouseDown={(e) => e.preventDefault()} onClick={() => runCommand("justifyCenter")} title="Align center">
              <span className="rte-align rte-align-center"><span /><span /><span /><span /></span>
            </button>
            <button type="button" className="btn btn-outline-secondary" onMouseDown={(e) => e.preventDefault()} onClick={() => runCommand("justifyRight")} title="Align right">
              <span className="rte-align rte-align-right"><span /><span /><span /><span /></span>
            </button>
            <button type="button" className="btn btn-outline-secondary" onMouseDown={(e) => e.preventDefault()} onClick={() => runCommand("justifyFull")} title="Justify">
              <span className="rte-align rte-align-justify"><span /><span /><span /><span /></span>
            </button>
          </div>
        </div>
      )}

      <div
        ref={editorRef}
        className="form-control rte-editable"
        style={{ minHeight }}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={emitChange}
        onBlur={emitChange}
        data-placeholder={placeholder}
      />

      <style>{`
        .rte-toggle {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          background: none;
          border: none;
          padding: 0 0 4px;
          font-size: 12px;
          color: #6c757d;
          cursor: pointer;
        }
        .rte-toggle:disabled { cursor: default; opacity: 0.6; }
        .rte-toggle:hover:not(:disabled) { color: #02bc9c; }
        .rte-caret { display: inline-block; transition: transform 0.15s ease; font-size: 10px; }
        .rte-caret-open { transform: rotate(90deg); }
        .rte-toolbar { display: flex; align-items: center; margin-bottom: 6px; }
        .rte-align { display: inline-flex; flex-direction: column; gap: 2px; width: 14px; vertical-align: middle; }
        .rte-align span { display: block; height: 2px; background: currentColor; border-radius: 1px; }
        .rte-align-left span:nth-child(1) { width: 100%; }
        .rte-align-left span:nth-child(2) { width: 65%; }
        .rte-align-left span:nth-child(3) { width: 100%; }
        .rte-align-left span:nth-child(4) { width: 45%; }
        .rte-align-center span:nth-child(1) { width: 100%; }
        .rte-align-center span:nth-child(2) { width: 60%; margin-left: 20%; }
        .rte-align-center span:nth-child(3) { width: 100%; }
        .rte-align-center span:nth-child(4) { width: 45%; margin-left: 27.5%; }
        .rte-align-right span:nth-child(1) { width: 100%; }
        .rte-align-right span:nth-child(2) { width: 65%; margin-left: 35%; }
        .rte-align-right span:nth-child(3) { width: 100%; }
        .rte-align-right span:nth-child(4) { width: 45%; margin-left: 55%; }
        .rte-align-justify span { width: 100%; }
        .rte-editable {
          min-height: 78px;
          overflow-y: auto;
          white-space: pre-wrap;
          word-break: break-word;
        }
        .rte-editable:empty:before {
          content: attr(data-placeholder);
          color: #6c757d;
          pointer-events: none;
        }
        .rte-disabled .rte-editable { background-color: #e9ecef; opacity: 1; }
      `}</style>
    </div>
  );
}
